import { createContext, lazy, Suspense, useContext, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Spin } from 'antd';
import { BellSlashIcon, XIcon } from '@phosphor-icons/react';
import { useHubAlertSetup, type HubAlertSetup } from './useHubAlertSetup';

const HubAlertSetupPage = lazy(() => import('./HubAlertSetupPage'));

export const SETUP_PATH = '/alerts-setup';

const SetupContext = createContext<HubAlertSetup | null>(null);

/** One live readiness check for the page area and the header (gate, strip, alert status). */
export const HubAlertSetupProvider = ({ children }: { children: ReactNode }) => (
  <SetupContext.Provider value={useHubAlertSetup()}>{children}</SetupContext.Provider>
);

export const useSharedHubAlertSetup = () => {
  const setup = useContext(SetupContext);
  if (!setup) throw new Error('useSharedHubAlertSetup needs HubAlertSetupProvider');
  return setup;
};

// Phones: no strip. The header bell is already red with a slash and its drawer has "Set up now"; a second
// red band on every page (with the Waiting bar on top) cost ~120px of a small screen. A CSS media query
// (not a breakpoint hook) keeps it render-safe everywhere.
export const STRIP_CSS = `
.hub-alert-strip { display: flex; align-items: center; gap: 12px; padding: 8px 16px; background: #B42318;
  color: #fff; cursor: pointer; }
.hub-alert-strip .hub-alert-strip-text { flex: 1; min-width: 0; font-weight: 600; }
.hub-alert-strip .hub-alert-strip-hide { min-width: 48px; min-height: 48px; margin: -8px -12px -8px 0; }
.hub-alert-strip-quiet { display: flex; align-items: center; gap: 6px; padding: 2px 12px; background: #f2f4f7;
  color: #475467; font-size: 12px; line-height: 20px; cursor: pointer; border-bottom: 1px solid #eaecf0; }
@media (max-width: 767px) {
  /* Phones: no strip; the header bell is red with a slash and its drawer has Set up now (one banner at a time). */
  .hub-alert-strip, .hub-alert-strip-quiet { display: none; }
}`;

/** The strip, once closed, stays hidden on this device for the rest of the day; the red bell still shows it. */
const HIDDEN_KEY = 'hub.alertStrip.hiddenOn';
const today = () => new Date().toDateString();
const hiddenToday = () => {
  try {
    return localStorage.getItem(HIDDEN_KEY) === today();
  } catch {
    return false;
  }
};
const hideToday = () => {
  try {
    localStorage.setItem(HIDDEN_KEY, today());
  } catch {
    // Storage blocked: hidden until the page reloads.
  }
};

/** Soft mode: one slim red line on every Hub page until this device is set up, or closed for the day. */
export const HubAlertSetupStrip = ({
  onOpen,
  onHide,
}: {
  onOpen: () => void;
  onHide?: () => void;
}) => (
  <div role="status" className="hub-alert-strip" onClick={onOpen}>
    <style>{STRIP_CSS}</style>
    <BellSlashIcon size={18} weight="bold" aria-hidden />
    <span className="hub-alert-strip-text">
      Order alerts are not set up on this device. You may miss new orders.
    </span>
    <Button
      size="small"
      onClick={event => {
        event.stopPropagation();
        onOpen();
      }}
      style={{ color: '#B42318', fontWeight: 700 }}
    >
      Set up now
    </Button>
    {onHide && (
      <Button
        className="hub-alert-strip-hide"
        type="text"
        aria-label="Hide for today"
        icon={<XIcon size={16} weight="bold" color="#fff" />}
        onClick={event => {
          event.stopPropagation();
          onHide();
        }}
      />
    )}
  </div>
);

/**
 * On work screens: one slim grey line, not the red bar (round 18: the red bar took a fifth of every floor screen and
 * used the colour of real refusals). Tapping it opens the setup; the full prompt stays on home.
 */
export const HubAlertSetupQuiet = ({ onOpen }: { onOpen: () => void }) => (
  <div role="status" className="hub-alert-strip-quiet" onClick={onOpen}>
    <style>{STRIP_CSS}</style>
    <BellSlashIcon size={12} weight="bold" aria-hidden />
    <span>Order alerts off · Set up</span>
  </div>
);

/**
 * Wraps Hub's page area. Soft mode: the strip above every page. Hard mode: the setup page instead
 * of any page until this device is ready (the header, with logout, stays). Alerts keep running.
 */
export const HubAlertSetupGate = ({ children }: { children: ReactNode }) => {
  const setup = useSharedHubAlertSetup();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(hiddenToday);

  if (setup.holding) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (setup.gate) {
    return (
      <Suspense fallback={<Spin style={{ display: 'block', margin: 48 }} />}>
        <HubAlertSetupPage variant="gate" />
      </Suspense>
    );
  }
  return (
    <>
      {setup.showStrip && !hidden && pathname !== SETUP_PATH && pathname !== '/' && (
        <HubAlertSetupQuiet onOpen={() => navigate(SETUP_PATH)} />
      )}
      {setup.showStrip && !hidden && pathname === '/' && (
        <HubAlertSetupStrip
          onOpen={() => navigate(SETUP_PATH)}
          onHide={() => {
            hideToday();
            setHidden(true);
          }}
        />
      )}
      {children}
    </>
  );
};
